import { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

export interface PropertyProfile {
  id: string;
  name: string;
  address?: string | null;
  accessNotes?: string | null;
  parkingNotes?: string | null;
  defaultTimeWindow?: string | null;
}

interface PropertyProfileSelectorProps {
  onPropertySelected: (property: PropertyProfile | null) => void;
}

export default function PropertyProfileSelector({ onPropertySelected }: PropertyProfileSelectorProps) {
  const [selectedId, setSelectedId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [properties, setProperties] = useState<PropertyProfile[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        setLoading(true);
        const { data: sessionData } = await supabase.auth.getSession();
        const session = sessionData?.session;
        if (!session) {
          if (isActive) setProperties([]);
          return;
        }
        // Resolve profile.id from auth user
        const { data: prof } = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", session.user.id)
          .maybeSingle();
        const profileId = prof?.id;
        if (!profileId) {
          if (isActive) setProperties([]);
          return;
        }
        const { data: addrs } = await (supabase as any)
          .from("service_addresses")
          .select("id,label,address_line1,address_line2,city,postal_code,country,is_active")
          .eq("profile_id", profileId)
          .eq("is_active", true)
          .order("created_at", { ascending: false });
        const toDisplay = (addrs || []).map(a => ({
          id: a.id,
          name: a.label,
          address: [a.address_line1, a.address_line2, `${a.postal_code} ${a.city}`, a.country].filter(Boolean).join(", "),
        } as PropertyProfile));
        if (isActive) setProperties(toDisplay);
      } finally {
        if (isActive) setLoading(false);
      }
    })();
    return () => { isActive = false; };
  }, []);

  const selected = useMemo(() => properties.find((p) => p.id === selectedId) || null, [properties, selectedId]);

  // Hide entirely if not logged-in or no saved addresses
  if (!loading && properties.length === 0) {
    return null;
  }

  return (
    <Card className="border-gray-200">
      <CardHeader>
        <CardTitle className="text-base">Property</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select
            value={selectedId}
            onValueChange={(val) => {
              setSelectedId(val);
              const prop = properties.find((p) => p.id === val) || null;
              onPropertySelected(prop || null);
            }}
          >
            <SelectTrigger className="md:col-span-2 border-gray-300">
              <SelectValue placeholder="Select a property" />
            </SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            className="w-full border-gray-300"
            onClick={() => navigate("/profile#addresses")}
          >
            Add/Manage addresses
          </Button>
        </div>

        {selected && (
          <div className="text-sm text-gray-600 space-y-1">
            <div><span className="text-gray-500">Address:</span> {selected.address}</div>
            {selected.defaultTimeWindow && (
              <div><span className="text-gray-500">Preferred Window:</span> {selected.defaultTimeWindow}</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

